'use client';

import type { MouseEvent } from 'react';
import type { StorePlace } from './homepage-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMemo, useState } from 'react';
import { MailIcon, MapIcon, PhoneIcon } from './ui/icons';

type StoreLocatorProps = {
  title: string;
  searchPlaceholder: string;
  emptyLabel: string;
  places: StorePlace[];
};

const OVERVIEW_ZOOM = 9;
const DETAIL_ZOOM = 14;

const mapUrl = (place: StorePlace, zoom: number) =>
  `https://maps.google.com/maps?q=${place.latitude},${place.longitude}&z=${zoom}&output=embed`;

const hasCoordinates = (place: StorePlace) => place.latitude !== null && place.longitude !== null;

const searchableText = (place: StorePlace) =>
  [place.name, place.street, place.locality, place.phone, place.email].join(' ').toLowerCase();

export function StoreLocator({ title, searchPlaceholder, emptyLabel, places }: StoreLocatorProps) {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState<number | null>(null);

  const term = query.trim().toLowerCase();
  const visibleIds = useMemo(
    () =>
      new Set(
        places
          .filter(place => !term || searchableText(place).includes(term))
          .map(place => place.id),
      ),
    [places, term],
  );

  const activePlace = places.find(place => place.id === activeId && hasCoordinates(place));
  const mapPlace = activePlace ?? places.find(hasCoordinates);

  if (!places.length) return null;

  const selectPlace = (event: MouseEvent<HTMLDivElement>, place: StorePlace) => {
    if ((event.target as HTMLElement).closest('a')) return;
    setActiveId(place.id);
  };

  return (
    <section
      aria-label={title}
      className="grid w-full grid-cols-1 bg-[#ebebeb] min-[641px]:grid-cols-2"
    >
      <div className="flex justify-end px-6 pt-12 min-[641px]:pl-6 min-[641px]:pr-12 min-[641px]:pt-20">
        <div className="flex w-full max-w-[568px] flex-col gap-8">
          <h2 className="relative m-0 pt-3.5 text-[clamp(1.7rem,3vw,2.25rem)] font-bold leading-[1.15] text-foreground before:absolute before:left-0 before:top-0 before:h-[3px] before:w-12 before:bg-accent before:content-['']">
            {title}
          </h2>
          <input
            type="text"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="h-12 w-full rounded-[8px] border border-input bg-white px-4 text-[.95rem] text-foreground focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-ring"
          />
          <div className="flex flex-col gap-6 pb-16">
            {places.map(place => (
              <div
                key={place.id}
                onClick={event => selectPlace(event, place)}
                className={cn(
                  'flex cursor-pointer flex-col gap-6 rounded-[8px] bg-white p-6 text-left transition-shadow duration-200',
                  place.id === activeId && 'shadow-[0_0_0_2px_hsl(var(--primary))]',
                  !visibleIds.has(place.id) && 'hidden',
                )}
              >
                <h3 className="m-0 text-[clamp(1.25rem,2.4vw,1.875rem)] font-semibold leading-[1.2] text-foreground">
                  <button
                    type="button"
                    aria-pressed={place.id === activeId}
                    className="[all:unset] focus-visible:[outline:2px_solid_hsl(var(--ring))]"
                  >
                    {place.name}
                  </button>
                </h3>
                <div className="flex flex-col gap-2">
                  <p className="m-0 flex items-start gap-2 text-[.875rem] text-primary">
                    <MapIcon />
                    <span>
                      {place.street},
                      <br />
                      {place.locality}
                    </span>
                  </p>
                  {place.phone && (
                    <a
                      href={`tel:${place.phone.replace(/\s+/g, '')}`}
                      className="flex items-start gap-2 text-[.875rem] hover:opacity-80"
                    >
                      <PhoneIcon />
                      <span>{place.phone}</span>
                    </a>
                  )}
                  {place.email && (
                    <a
                      href={`mailto:${place.email}`}
                      className="flex items-start gap-2 text-[.875rem] hover:opacity-80"
                    >
                      <MailIcon />
                      <span>{place.email}</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
            {visibleIds.size === 0 && (
              <div className="p-2 text-center text-[.875rem] text-[#999]">{emptyLabel}</div>
            )}
          </div>
        </div>
      </div>
      <div className="relative aspect-square min-[641px]:aspect-auto min-[641px]:min-h-[720px]">
        {mapPlace && (
          <iframe
            title={title}
            src={mapUrl(mapPlace, activePlace ? DETAIL_ZOOM : OVERVIEW_ZOOM)}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
          />
        )}
      </div>
    </section>
  );
}
